// covered-by: frontends/carrie/src/ask-v2/review/__tests__/pinGroups.test.ts
//
// The review's pins in groups (warhol spec dave.savostyanov/0090). Every
// finding keeps its own pin and source; a group is a way of reading them: by
// the block of the pack its raiser reads, or by the place on the pack the pins
// sit close together. Pure: no React, no i18n.
import { type AskPunttReviewGroupSummary } from "@mg/schemas/src/christo/askPunttReview";

import { type CommentWithOptionalTags } from "./reviewCommentTags";

import {
  reviewCommentIssue,
  reviewCommentTitle,
} from "../../components/ask-puntt-review/reviewCommentDisplay";

export type PinGroupBy = "block" | "place";

export const PIN_BLOCKS = [
  "nutrition",
  "ingredients-legal",
  "claims-front",
  "codes-barcode",
  "typography",
  "version-changes",
  "other",
] as const;
export type PinBlock = (typeof PIN_BLOCKS)[number];

// why a copy of warhol's `contracts/pin_groups.PIN_BLOCK`: a review written
// before warhol stamped `comment.group` still has to group. A comment that
// carries `group` is never read through this map.
const RAISER_BLOCK: Readonly<Record<string, PinBlock>> = {
  nutrition: "nutrition",
  "regulated-copy": "ingredients-legal",
  "mandatory-copy": "ingredients-legal",
  "copy-diff": "ingredients-legal",
  "line-diff": "ingredients-legal",
  diacritics: "ingredients-legal",
  "enumeration-order": "ingredients-legal",
  completeness: "ingredients-legal",
  "dangling-field": "ingredients-legal",
  correspondence: "claims-front",
  variants: "claims-front",
  consistency: "claims-front",
  "codes-values": "codes-barcode",
  identifier: "codes-barcode",
  "barcode-zone": "codes-barcode",
  typography: "typography",
  "version-change": "version-changes",
};

/** Where a place group sits on the page, so two groups of one block read apart. */
export type PinPlace = {
  row: "top" | "middle" | "bottom";
  col: "left" | "centre" | "right";
};

export type PinGroup = {
  key: string;
  block: PinBlock;
  /** Set when two groups of one block need telling apart (place grouping). */
  place?: PinPlace;
  /** Most serious first. */
  members: CommentWithOptionalTags[];
  /** The member drawn as the group's pin: the one nearest the group's middle. */
  hub: CommentWithOptionalTags;
  urgent: boolean;
  /** What is wrong in the group, one point per finding: warhol's for a block, its findings' Issue lines for a place. */
  points?: string[];
};

/** 2 for a finding to fix before print, 1 for a minor one, 0 for a point to check. */
export function severityRank(comment: CommentWithOptionalTags): number {
  if (comment.isRequired || comment.severity === "high") return 2;
  return comment.severity === "medium" ? 1 : 0;
}

/**
 * A finding the "Show minor and check findings" switch hides: one the review
 * rated below urgent.
 *
 * why the severity must be set: a person's comment carries none, and filed
 * with the minor findings it was hidden by the switch and could not be reached.
 */
export function isMinorFinding(comment: CommentWithOptionalTags): boolean {
  return Boolean(comment.severity) && severityRank(comment) < 2;
}

/** The letter a group goes by on its pin and its section: A to Z, then AA, AB, as spreadsheet columns. */
export function groupMark(index: number): string {
  let mark = "";
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    mark = String.fromCharCode(65 + ((n - 1) % 26)) + mark;
  }
  return mark;
}

/** How many of a group's findings are to fix before print, and how many are minor or check. */
export function clusterCounts(group: PinGroup): {
  critical: number;
  minor: number;
} {
  const minor = group.members.filter(isMinorFinding).length;
  return { critical: group.members.length - minor, minor };
}

/**
 * A pin's tone: washed out for a finding the review rated minor or check,
 * the full blue otherwise.
 *
 * why the severity is read and not `isMinorFinding`: a person's comment
 * carries none, and it is not a minor finding.
 */
export function severityPinTone(comment: {
  isRequired?: boolean | null;
  severity?: string | null;
}): "strong" | "soft" {
  const minor =
    !comment.isRequired &&
    (comment.severity === "medium" || comment.severity === "low");
  return minor ? "soft" : "strong";
}

export function pinBlockOf(comment: CommentWithOptionalTags): PinBlock {
  const stamped = comment.group as PinBlock | undefined;
  if (stamped && (PIN_BLOCKS as readonly string[]).includes(stamped)) {
    return stamped;
  }
  const raiser = (comment.tags?.[0] ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  return RAISER_BLOCK[raiser] ?? "other";
}

function bySeverity(
  comments: readonly CommentWithOptionalTags[],
): CommentWithOptionalTags[] {
  return [...comments].sort((a, b) => severityRank(b) - severityRank(a));
}

function point(comment: CommentWithOptionalTags): [number, number] | null {
  if (comment.rel_x == null || comment.rel_y == null) return null;
  return [comment.rel_x, comment.rel_y];
}

function hubOf(members: readonly CommentWithOptionalTags[]) {
  const placed = members.filter((m) => point(m) != null);
  if (placed.length === 0) return members[0];
  const mx =
    placed.reduce((sum, m) => sum + (point(m) as [number, number])[0], 0) /
    placed.length;
  const my =
    placed.reduce((sum, m) => sum + (point(m) as [number, number])[1], 0) /
    placed.length;
  return placed.reduce((best, m) => {
    const [x, y] = point(m) as [number, number];
    const [bx, by] = point(best) as [number, number];
    return (x - mx) ** 2 + (y - my) ** 2 < (bx - mx) ** 2 + (by - my) ** 2
      ? m
      : best;
  });
}

function makeGroup(
  key: string,
  block: PinBlock,
  members: readonly CommentWithOptionalTags[],
  points?: readonly string[],
): PinGroup {
  const sorted = bySeverity(members);
  return {
    key,
    block,
    members: sorted,
    hub: hubOf(sorted),
    urgent: sorted.some((m) => severityRank(m) === 2),
    ...(points?.length ? { points: [...points] } : {}),
  };
}

/** Most serious group first, then the larger one. */
export function orderGroups(groups: readonly PinGroup[]): PinGroup[] {
  return [...groups].sort(
    (a, b) =>
      severityRank(b.members[0]) - severityRank(a.members[0]) ||
      b.members.length - a.members.length,
  );
}

export function groupByBlock(
  comments: readonly CommentWithOptionalTags[],
  summaries: readonly AskPunttReviewGroupSummary[] = [],
): PinGroup[] {
  const byBlock = new Map<PinBlock, CommentWithOptionalTags[]>();
  for (const comment of comments) {
    const block = pinBlockOf(comment);
    byBlock.set(block, [...(byBlock.get(block) ?? []), comment]);
  }
  const lines = new Map(summaries.map((s) => [s.group, s.points]));
  return orderGroups(
    Array.from(byBlock.entries()).map(([block, members]) =>
      makeGroup(`block-${block}`, block, members, lines.get(block)),
    ),
  );
}

// Pins whose centres are this close (page fractions) join one place group.
const LINK = 0.09;
// why a size cap and not plain single linkage: on a dense label every pin is
// near the next one, and a chain swallowed 25 of 37 findings into one group in
// the prototype. A place is what a person takes in at one glance.
const MAX_W = 0.24;
const MAX_H = 0.14;

/** Agglomerative places of one page's pins: the closest two merge while the place stays one glance wide. */
export function placeClusters(points: readonly [number, number][]): number[][] {
  const clusters = points.map((_, i) => [i]);
  const gap = (a: number[], b: number[]) =>
    Math.min(
      ...a.flatMap((i) =>
        b.map((j) =>
          Math.hypot(points[i][0] - points[j][0], points[i][1] - points[j][1]),
        ),
      ),
    );
  const fits = (members: number[]) => {
    const xs = members.map((i) => points[i][0]);
    const ys = members.map((i) => points[i][1]);
    return (
      Math.max(...xs) - Math.min(...xs) <= MAX_W &&
      Math.max(...ys) - Math.min(...ys) <= MAX_H
    );
  };
  for (;;) {
    let best: [number, number, number] | null = null;
    for (let i = 0; i < clusters.length; i += 1) {
      for (let j = i + 1; j < clusters.length; j += 1) {
        const d = gap(clusters[i], clusters[j]);
        if (d > LINK || !fits([...clusters[i], ...clusters[j]])) continue;
        if (best == null || d < best[0]) best = [d, i, j];
      }
    }
    if (best == null) return clusters;
    const [, i, j] = best;
    clusters[i] = [...clusters[i], ...clusters[j]];
    clusters.splice(j, 1);
  }
}

function placeOf(members: readonly CommentWithOptionalTags[]): PinPlace {
  const placed = members.map(point).filter((p) => p != null) as [
    number,
    number,
  ][];
  const mx = placed.reduce((s, p) => s + p[0], 0) / placed.length;
  const my = placed.reduce((s, p) => s + p[1], 0) / placed.length;
  return {
    row: my < 0.34 ? "top" : my > 0.66 ? "bottom" : "middle",
    col: mx < 0.34 ? "left" : mx > 0.66 ? "right" : "centre",
  };
}

function mostCommonBlock(
  members: readonly CommentWithOptionalTags[],
): PinBlock {
  const counts = new Map<PinBlock, number>();
  for (const m of members) {
    const block = pinBlockOf(m);
    counts.set(block, (counts.get(block) ?? 0) + 1);
  }
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0];
}

// why 4 and 2: warhol's `group_summary.MAX_POINTS` and `MIN_GROUP`, so a place reads like a block.
const MAX_PLACE_POINTS = 4;
const MIN_PLACE_POINTS = 2;

/** A place's points: its findings' Issue lines, most serious first; none for a place of one finding. */
export function placePoints(
  members: readonly CommentWithOptionalTags[],
): string[] | undefined {
  if (members.length < MIN_PLACE_POINTS) return undefined;
  const lines = bySeverity(members).map(
    (m) => reviewCommentIssue(m) ?? reviewCommentTitle(m),
  );
  return Array.from(new Set(lines)).slice(0, MAX_PLACE_POINTS);
}

export function groupByPlace(
  comments: readonly CommentWithOptionalTags[],
): PinGroup[] {
  const byPage = new Map<number, CommentWithOptionalTags[]>();
  const unplaced: CommentWithOptionalTags[] = [];
  for (const comment of comments) {
    if (point(comment) == null) unplaced.push(comment);
    else {
      const page = comment.page_num ?? 1;
      byPage.set(page, [...(byPage.get(page) ?? []), comment]);
    }
  }
  const groups: PinGroup[] = [];
  for (const [page, members] of Array.from(byPage.entries())) {
    const clusters = placeClusters(
      members.map((m) => point(m) as [number, number]),
    );
    clusters.forEach((cluster, index) => {
      const inside = cluster.map((i) => members[i]);
      groups.push(
        makeGroup(
          `place-${page}-${index}`,
          mostCommonBlock(inside),
          inside,
          placePoints(inside),
        ),
      );
    });
  }
  if (unplaced.length > 0) {
    groups.push(
      makeGroup(
        "place-none",
        mostCommonBlock(unplaced),
        unplaced,
        placePoints(unplaced),
      ),
    );
  }
  // A block two places share gets its place, so the list tells them apart.
  // why not the unplaced group: it sits nowhere, and its centre of no pins read "top left".
  const shared = (block: PinBlock) =>
    groups.filter((g) => g.block === block).length > 1;
  return orderGroups(
    groups.map((g) =>
      g.key !== "place-none" && shared(g.block)
        ? { ...g, place: placeOf(g.members) }
        : g,
    ),
  );
}

/** Every finding in group order, most serious first inside each: the order the list and the pins number. */
export function groupedComments(
  groups: readonly PinGroup[],
): CommentWithOptionalTags[] {
  return groups.flatMap((g) => g.members);
}

/** A review's findings with each typography finding on another block's finding set inside that finding. */
export type Companions = {
  /** Every finding but the companions, in the order given. */
  leads: CommentWithOptionalTags[];
  /** A lead's companions, in the order given. */
  companionsOf: ReadonlyMap<string, CommentWithOptionalTags[]>;
  /** The lead a companion is shown in. */
  leadOf: ReadonlyMap<string, string>;
};

// why 0.3: the place rule warhol's duplicate judge asks about
// (`agents/artwork/duplicate_judge.py::_place_overlap`, `DUPLICATE_OVERLAP`).
const SAME_SPOT = 0.3;

/** How much two findings sit on one spot: 1 when a pin is in the other's box, else overlap over the smaller box. */
function spotOverlap(
  a: CommentWithOptionalTags,
  b: CommentWithOptionalTags,
): number {
  if ((a.page_num ?? 1) !== (b.page_num ?? 1)) return 0;
  const inBox = (
    p: CommentWithOptionalTags,
    box: NonNullable<CommentWithOptionalTags["bbox"]>,
  ) =>
    p.rel_x != null &&
    p.rel_y != null &&
    p.rel_x >= box.x &&
    p.rel_x <= box.x + box.w &&
    p.rel_y >= box.y &&
    p.rel_y <= box.y + box.h;
  if ((b.bbox && inBox(a, b.bbox)) || (a.bbox && inBox(b, a.bbox))) return 1;
  if (!a.bbox || !b.bbox) return 0;
  const w =
    Math.min(a.bbox.x + a.bbox.w, b.bbox.x + b.bbox.w) -
    Math.max(a.bbox.x, b.bbox.x);
  const h =
    Math.min(a.bbox.y + a.bbox.h, b.bbox.y + b.bbox.h) -
    Math.max(a.bbox.y, b.bbox.y);
  const smaller = Math.min(a.bbox.w * a.bbox.h, b.bbox.w * b.bbox.h);
  return w > 0 && h > 0 && smaller > 0 ? (w * h) / smaller : 0;
}

/**
 * Sets a typography finding inside the finding of another block that sits on
 * its spot, most serious first: the space missing in "GLUTEN(TRIGO)" read once
 * against the approved copy and once as typesetting is one place to fix.
 *
 * why typography only: warhol's judge keeps most same-spot pairs apart
 * because they are two defects (trans fat and fibre missing from one row);
 * a typography note on a copy finding's spot is how that same text is set.
 * Nothing is dropped: the companion keeps its own text and judgment.
 */
export function attachCompanions(
  comments: readonly CommentWithOptionalTags[],
): Companions {
  const finding = (c: CommentWithOptionalTags) =>
    c.isAI !== false && c.severity != null;
  const leadOf = new Map<string, string>();
  for (const companion of comments) {
    if (!finding(companion) || pinBlockOf(companion) !== "typography") continue;
    let best: { lead: CommentWithOptionalTags; overlap: number } | null = null;
    for (const lead of comments) {
      if (
        lead === companion ||
        !finding(lead) ||
        pinBlockOf(lead) === "typography"
      )
        continue;
      const overlap = spotOverlap(companion, lead);
      if (overlap < SAME_SPOT) continue;
      if (
        best == null ||
        severityRank(lead) > severityRank(best.lead) ||
        (severityRank(lead) === severityRank(best.lead) &&
          overlap > best.overlap)
      ) {
        best = { lead, overlap };
      }
    }
    if (best) leadOf.set(companion._id, best.lead._id);
  }
  const companionsOf = new Map<string, CommentWithOptionalTags[]>();
  for (const comment of comments) {
    const lead = leadOf.get(comment._id);
    if (lead != null)
      companionsOf.set(lead, [...(companionsOf.get(lead) ?? []), comment]);
  }
  return {
    leads: comments.filter((c) => !leadOf.has(c._id)),
    companionsOf,
    leadOf,
  };
}

export type PinOrderOptions = {
  /** The pin groups flag; off, the comments come back as given. */
  enabled: boolean;
  /** False hides every minor and check finding from the list and the pins. */
  showMinor: boolean;
  /** "Groups" rather than "All pins". */
  grouping: boolean;
  groupBy: PinGroupBy;
  summaries?: readonly AskPunttReviewGroupSummary[];
  /** An open finding: grouped and numbered first. The decided ones follow. */
  isOpen: (comment: CommentWithOptionalTags) => boolean;
};

export type PinOrder = {
  companions: Companions;
  /** The groups the open findings make, whether or not the view groups them. */
  allGroups: PinGroup[];
  /** `allGroups` when the view groups, else none. */
  groups: PinGroup[];
  /** The order the list and the pins number: group by group, or to-fix first. */
  ordered: CommentWithOptionalTags[];
};

/**
 * The findings of one review as the pin view shows them: minor and check ones
 * hidden or not, typography companions folded into their lead, numbered group
 * by group or, in "All pins", the open findings to fix before the minor and
 * check ones, then the decided findings.
 *
 * why one function: the open review and the version beside it in a comparison
 * both read it, so one finding never shows as one pin on one side and two on
 * the other.
 */
export function orderPins(
  comments: readonly CommentWithOptionalTags[],
  { enabled, showMinor, grouping, groupBy, summaries, isOpen }: PinOrderOptions,
): PinOrder {
  const shown = showMinor
    ? [...comments]
    : comments.filter((c) => !isMinorFinding(c));
  // A typography finding on another open finding's spot is drawn inside that
  // finding's card, so it leaves the groups, the numbers and the pins.
  const companions: Companions = enabled
    ? attachCompanions(shown.filter(isOpen))
    : { leads: [], companionsOf: new Map(), leadOf: new Map() };
  const leads = shown.filter((c) => !companions.leadOf.has(c._id));
  const open = leads.filter(isOpen);
  const decided = leads.filter((c) => !isOpen(c));
  // why: counted in "All pins" too, so the switch names the groups she would get.
  const allGroups = !enabled
    ? []
    : groupBy === "place"
      ? groupByPlace(open)
      : groupByBlock(open, summaries ?? []);
  const groups = grouping ? allGroups : [];
  const ordered = grouping
    ? [...groupedComments(groups), ...decided]
    : !enabled
      ? leads
      : [
          ...open.filter((c) => !isMinorFinding(c)),
          ...open.filter(isMinorFinding),
          ...decided,
        ];
  return { companions, allGroups, groups, ordered };
}
